import { useContext, useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { SessionContext } from '../../contexts/SessionContext';

// Global flag to track when session expiry is in progress
let sessionExpiryInProgress = false;
const sessionExpiryListeners = new Set<() => void>();

// Export function to allow request handler to set this flag
export const setSessionExpiryInProgress = (value: boolean) => {
  sessionExpiryInProgress = value;
  // Notify all listeners immediately
  sessionExpiryListeners.forEach((listener) => listener());
};

export const UnsavedChangesFormPrompt = ({ hasUnsavedChanges }: { hasUnsavedChanges: boolean }) => {
  const { sessionExists } = useContext(SessionContext);
  const [sessionExpiryDetected, setSessionExpiryDetected] = useState(sessionExpiryInProgress);

  // Register listener for session expiry changes
  useEffect(() => {
    const listener = () => {
      setSessionExpiryDetected(sessionExpiryInProgress);
    };

    sessionExpiryListeners.add(listener);
    return () => {
      sessionExpiryListeners.delete(listener);
    };
  }, []);

  const shouldPrompt = hasUnsavedChanges && sessionExists !== false && !sessionExpiryDetected;

  // Confirm navigation if there are unsaved changes for browser events
  useEffect(() => {
    const onBeforeUnload = (e: Event) => {
      if (shouldPrompt) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [shouldPrompt]);

  // Confirm navigation if there are unsaved changes for react-router events
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      shouldPrompt &&
      currentLocation.pathname !== nextLocation.pathname &&
      !confirm('You have unsaved changes. Are you sure you want to leave this page?')
  );

  // Reset blocker when blocked - no benefit to stay on blocked state
  // The confirmation is done inside the useBlocker call
  useEffect(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker, blocker.state]);

  return null;
};
